// Single API base URL from frontend/.env (REACT_APP_API_BASE_URL)
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Enhanced fetch function with retry capability
const fetchWithRetry = async (url, options = {}, retries = 1) => {
  try {
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    if (retries > 0 && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
      console.log(`Connection failed. Retrying... (${retries} retries left)`);
      return fetchWithRetry(url, options, retries - 1);
    }
    throw error;
  }
};

// Function to fetch debug table info
export const fetchTableDebugInfo = async () => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/debug/table-info`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch table debug info');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching table debug info:', error);
    throw error;
  }
};

// Function to fetch user info by UserPK
export const fetchUserInfo = async (userPK) => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/user-info/${userPK}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch user info');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user info:', error);
    throw error;
  }
};

// Function to fetch user vehicle data by UserPK
export const fetchUserVehicles = async (userPK) => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/user-vehicles/${userPK}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch user vehicles');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user vehicles:', error);
    throw error;
  }
};

// Function to fetch user data by employee ID
export const fetchUserByEmployeeId = async (employeeId) => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ employeeId }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch user data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
};

// Function to fetch simplified user info by UserPK
export const fetchSimpleUserInfo = async (userPK, tryPhoto = true) => {
  try {
    // Add query param to try fetching the photo if requested
    const queryParam = tryPhoto ? '?tryPhoto=true' : '';
    const response = await fetchWithRetry(`${API_BASE_URL}/user-simple/${userPK}${queryParam}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch user info');
    }
    
    const data = await response.json();
    
    // Log diagnostics if available
    if (data.diagnostics) {
      console.log('User photo diagnostics:', data.diagnostics);
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching simple user info:', error);
    throw error;
  }
};

// Function to fetch unique violations and calculate scores
export const fetchUniqueViolationsAndScores = async (userPK, vehicleID, locationID) => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/get-unique-violations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userPK, vehicleID, locationID }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch violations and scores');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching violations and scores:', error);
    throw error;
  }
};

// Function to refresh the violations and scores cache
export const refreshViolationsCache = async (userPK, vehicleID, locationID) => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/refresh-violations-cache`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userPK, vehicleID, locationID }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to refresh violations cache');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error refreshing violations cache:', error);
    throw error;
  }
};

// Function to fetch maximum session number for a user and vehicle type
export const fetchSessionOptions = async (userPK, vehicleName) => {
  try {
    // Using URL parameters to safely pass the values for the SQL query placeholders
    const response = await fetchWithRetry(`${API_BASE_URL}/session-options?userPK=${encodeURIComponent(userPK)}&vehicleName=${encodeURIComponent(vehicleName)}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch session options');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching session options:', error);
    throw error;
  }
};

// Function to fetch vehicle assessment data by UserPK and VehicleName
export const fetchVehicleAssessmentData = async (userPK, vehicleName, sessionNumber = null) => {
  try {
    // Using URL parameters to safely pass the values for the SQL query placeholders
    let url = `${API_BASE_URL}/vehicle-assessment-data?userPK=${encodeURIComponent(userPK)}&vehicleName=${encodeURIComponent(vehicleName)}`;
    
    // Add sessionNumber parameter if provided
    if (sessionNumber !== null) {
      url += `&sessionNumber=${encodeURIComponent(sessionNumber)}`;
    }
    
    const response = await fetchWithRetry(url);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch vehicle assessment data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching vehicle assessment data:', error);
    throw error;
  }
};

// Function to authenticate user with EmployeeID and Password
export const loginUser = async (employeeID, password) => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ employeeID, password }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Invalid credentials');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error during login:', error);
    throw error;
  }
};

// Function to fetch detailed violation data for a specific attempt
export const fetchViolationDetails = async (userPK, assessmentNumber, attemptNumber, vehicleID) => {
  try {
    // Using URL parameters to safely pass the values for the SQL query placeholders
    const url = `${API_BASE_URL}/violation-details?userPK=${encodeURIComponent(userPK)}&assessmentNumber=${encodeURIComponent(assessmentNumber)}&attemptNumber=${encodeURIComponent(attemptNumber)}&vehicleID=${encodeURIComponent(vehicleID)}`;
    
    const response = await fetchWithRetry(url);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch violation details');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching violation details:', error);
    throw error;
  }
};

// Function to fetch all vehicle remarks
export const fetchVehicleRemarks = async () => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/vehicle-remarks`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch vehicle remarks');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching vehicle remarks:', error);
    throw error;
  }
};

// Function to check Remarks content in user_vehicle table
export const checkRemarks = async () => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/check-remarks`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to check remarks data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking remarks data:', error);
    throw error;
  }
};

// Function to check user vehicle remarks
export const checkUserVehicleRemarks = async () => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/check-remarks`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to check user vehicle remarks');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking user vehicle remarks:', error);
    throw error;
  }
};

// Function to fetch database diagnostics
export const fetchDatabaseDiagnostics = async () => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/diagnostics/database`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch database diagnostics');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching database diagnostics:', error);
    throw error;
  }
};

// Function to fetch vehicle leaderboard data
export const fetchVehicleLeaderboard = async (vehicleID, userPK) => {
  try {
    console.log('fetchVehicleLeaderboard called with:', { vehicleID, userPK });
    
    const url = `${API_BASE_URL}/vehicle-leaderboard?vehicleID=${encodeURIComponent(vehicleID)}&userPK=${encodeURIComponent(userPK)}`;
    
    console.log('Sending request to:', url);
      
    const response = await fetchWithRetry(url);
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Leaderboard API error:', data);
      
      // Try fallback to test endpoint if main fails
      console.log('Trying fallback test endpoint...');
      const fallbackUrl = `${API_BASE_URL}/test-leaderboard?vehicleID=${encodeURIComponent(vehicleID)}&userPK=${encodeURIComponent(userPK)}`;
      const fallbackResponse = await fetchWithRetry(fallbackUrl);
      
      if (!fallbackResponse.ok) {
        throw new Error(`Failed to fetch vehicle leaderboard: ${data.error || response.statusText}`);
      }
      
      const fallbackData = await fallbackResponse.json();
      console.log('Using fallback test data:', fallbackData);
      return fallbackData;
    }
    
    console.log('Leaderboard API response:', data);
    
    // Make sure we have what we need
    if (!data.leaderboard || !Array.isArray(data.leaderboard)) {
      console.error('Invalid leaderboard data format:', data);
      throw new Error('Invalid leaderboard data format');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching vehicle leaderboard:', error);
    
    // Return mock data as absolute fallback
    const mockData = {
      leaderboard: [
        { Rankings: 1, EmployeeName: 'Top Player', UserPK: 100 },
        { Rankings: 2, EmployeeName: 'Runner Up', UserPK: 101 },
        { Rankings: 3, EmployeeName: 'Third Place', UserPK: 102 }
      ],
      userRank: 3,
      source: 'error-fallback'
    };
    
    console.log('Returning mock data due to error');
    return mockData;
  }
};

// Function to fetch user assessment details final data
export const fetchUserAssessmentDetails = async (userPK) => {
  try {
    const url = `${API_BASE_URL}/user-assessment-details-final?userPK=${encodeURIComponent(userPK)}`;
    console.log('Fetching user assessment details from:', url);
    
    const response = await fetchWithRetry(url);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch user assessment details');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching user assessment details:', error);
    throw error;
  }
};

// Function to register a new user
export const signupUser = async (userData) => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Registration failed');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error during signup:', error);
    throw error;
  }
};

// Function to fetch working areas for signup
export const fetchWorkingAreas = async (locationID = 1) => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/working-areas?locationID=${locationID}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch working areas');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching working areas:', error);
    throw error;
  }
};

// Function to run the update remarks script
export const runUpdateScript = async () => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/run-update-script`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    
    return await response.json();
  } catch (error) {
    console.error('Error running update script:', error);
    throw error;
  }
};
